import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useSWRConfig } from 'swr';
import { createDocumentSelection } from '@/components/case-partner/hooks/useDocumentSelections';
import type { DocumentFilterToggle, SelectionDisabledResult } from '@/components/document-selector/DocumentSelector';
import { DocumentSelector } from '@/components/document-selector/DocumentSelector';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { AnalysisSetStatus, Document } from '@/hooks/useDocuments';
import { apiErrorMessage } from '@/lib/api-client';
import type { DocumentSelection } from '@/types';
import { AnalysisSetBadge } from './AnalysisSetBadge';
import { useDeepAnalysisCostConfirmation } from './DeepAnalysisCostConfirmation';
import { DEEP_ANALYSIS_LABEL, setFileAnalysisSetState } from '@/hooks/useAnalysisSet';

interface DeepAnalysisDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterGuid: string;
  onChanged?: () => void | Promise<void>;
}

const ACTIVE_ANALYSIS_SET_STATUSES = new Set<AnalysisSetStatus>([
  'included',
  'queued',
  'processing',
  'current',
]);
const VISIBLE_INACTIVE_ANALYSIS_SET_STATUSES = new Set<AnalysisSetStatus>([
  'removing',
  'error',
]);

export function documentIsInDeepAnalysis(doc: Document) {
  return doc.analysisSetEffective === true
    || (doc.analysisSetStatus ? ACTIVE_ANALYSIS_SET_STATUSES.has(doc.analysisSetStatus) : false);
}

function visibleAnalysisSetStatus(doc: Document): AnalysisSetStatus | null {
  const status = doc.analysisSetStatus;
  if (status && (ACTIVE_ANALYSIS_SET_STATUSES.has(status) || VISIBLE_INACTIVE_ANALYSIS_SET_STATUSES.has(status))) {
    return status;
  }
  return doc.analysisSetEffective ? 'included' : null;
}

export const DeepAnalysisDocumentDialog: React.FC<DeepAnalysisDocumentDialogProps> = ({
  open,
  onOpenChange,
  matterGuid,
  onChanged,
}) => {
  const { mutate } = useSWRConfig();
  const {
    confirmDeepAnalysisCost,
    deepAnalysisCostConfirmationDialog,
  } = useDeepAnalysisCostConfirmation();
  const [selectedDocs, setSelectedDocs] = useState<Map<string, Document>>(new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const documentSelections = useMemo<Map<string, DocumentSelection>>(
    () => new Map(
      Array.from(selectedDocs.entries()).map(([guid, doc]) => [guid, createDocumentSelection(doc)]),
    ),
    [selectedDocs],
  );

  useEffect(() => {
    if (open) {
      setSelectedDocs(new Map());
      setIsSubmitting(false);
    }
  }, [open]);

  const toggleDocumentSelection = useCallback((doc: Document) => {
    if (documentIsInDeepAnalysis(doc)) return;
    setSelectedDocs(prev => {
      const next = new Map(prev);
      if (next.has(doc.guid)) {
        next.delete(doc.guid);
      } else {
        next.set(doc.guid, doc);
      }
      return next;
    });
  }, []);

  const clearSelections = useCallback(() => {
    setSelectedDocs(new Map());
  }, []);

  const isSelectionDisabled = useCallback((doc: Document): SelectionDisabledResult => {
    if (documentIsInDeepAnalysis(doc)) {
      return { disabled: true, reason: `Already in ${DEEP_ANALYSIS_LABEL}` };
    }
    if (doc.analysisSetStatus === 'removing') {
      return { disabled: true, reason: `${DEEP_ANALYSIS_LABEL} removal is still pending` };
    }
    return { disabled: false };
  }, []);

  const getDocumentIconOverlay = useCallback((doc: Document) => {
    const status = visibleAnalysisSetStatus(doc);
    const active = documentIsInDeepAnalysis(doc);
    if (!active && !status) return null;

    return (
      <AnalysisSetBadge
        active={active}
        status={status}
        compact
        iconOnly
      />
    );
  }, []);

  const filterToggles = useMemo<DocumentFilterToggle[]>(() => [
    {
      id: 'hide-deep-analysis',
      label: 'Hide included',
      defaultValue: false,
      predicate: (doc: Document) => !documentIsInDeepAnalysis(doc),
    },
  ], []);

  const handleDone = useCallback(async () => {
    const docs = Array.from(selectedDocs.values()).filter(doc => !documentIsInDeepAnalysis(doc));
    if (docs.length === 0 || isSubmitting) return;

    const confirmed = await confirmDeepAnalysisCost({
      documentCount: docs.length,
      tokenCount: docs.reduce((sum, doc) => sum + (doc.token_count || 0), 0),
    });
    if (!confirmed) return;

    setIsSubmitting(true);
    try {
      const results = await Promise.allSettled(
        docs.map(async (doc) => {
          await setFileAnalysisSetState(matterGuid, doc.id, 'included');
          return doc;
        }),
      );

      const succeededDocs = results.flatMap(result => result.status === 'fulfilled' ? [result.value] : []);
      const failedResults = results.filter((result): result is PromiseRejectedResult => result.status === 'rejected');

      if (succeededDocs.length > 0) {
        await mutate(
          key => typeof key === 'string' && key.startsWith(`/correspondence/${matterGuid}/fetch-all`),
        );
        await onChanged?.();
      }

      if (failedResults.length === 0) {
        toast.success(
          docs.length === 1
            ? `Added 1 document to ${DEEP_ANALYSIS_LABEL}`
            : `Added ${docs.length} documents to ${DEEP_ANALYSIS_LABEL}`,
        );
        setSelectedDocs(new Map());
        onOpenChange(false);
        return;
      }

      if (succeededDocs.length > 0) {
        const succeededGuids = new Set(succeededDocs.map(doc => doc.guid));
        setSelectedDocs(prev => {
          const next = new Map(prev);
          succeededGuids.forEach(guid => {
            next.delete(guid);
          });
          return next;
        });
        toast.success(
          succeededDocs.length === 1
            ? `Added 1 document to ${DEEP_ANALYSIS_LABEL}`
            : `Added ${succeededDocs.length} documents to ${DEEP_ANALYSIS_LABEL}`,
        );
      }

      const failedCount = failedResults.length;
      const errorMessage = apiErrorMessage(failedResults[0]?.reason);
      toast.error(
        errorMessage
          ? `${failedCount} document${failedCount === 1 ? '' : 's'} failed: ${errorMessage}`
          : `${failedCount} document${failedCount === 1 ? '' : 's'} failed to update`,
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [confirmDeepAnalysisCost, isSubmitting, matterGuid, mutate, onChanged, onOpenChange, selectedDocs]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[95vw] w-[95vw] max-h-[90vh] h-[90vh] p-0 gap-0 flex flex-col">
          <DialogHeader className="px-6 py-4 border-b border-gray-200 shrink-0">
            <DialogTitle className="text-lg font-semibold">{DEEP_ANALYSIS_LABEL} Documents</DialogTitle>
            <DialogDescription className="text-sm text-gray-500 mt-1">
              Browse matter files and add documents to {DEEP_ANALYSIS_LABEL}.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-hidden">
            <DocumentSelector
              matterSlug={matterGuid}
              documentSelections={documentSelections}
              onToggleSelection={toggleDocumentSelection}
              onClearSelection={clearSelections}
              showUploadButton={false}
              onDone={handleDone}
              doneLabel={isSubmitting ? 'Adding...' : `Add to ${DEEP_ANALYSIS_LABEL}`}
              doneDisabled={selectedDocs.size === 0 || isSubmitting}
              getDocumentIconOverlay={getDocumentIconOverlay}
              isSelectionDisabled={isSelectionDisabled}
              filterToggles={filterToggles}
              className="h-full"
            />
          </div>
        </DialogContent>
      </Dialog>
      {deepAnalysisCostConfirmationDialog}
    </>
  );
};
